import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import { readRtkQueryError } from "_shared/_errors/api-error";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { useState } from "react";
import PasswordInput from "users/auth/credentials/passwords/_views/password-input";
import type Password from "users/auth/credentials/passwords/password";
import { useChangePasswordMutation } from "users/settings/_api/password-change-api";
import SettingsSection from "users/settings/_views/settings-section";

export const WRONG_CURRENT_PASSWORD_MESSAGE = "The current password is incorrect.";
export const UNCHANGED_PASSWORD_MESSAGE = "The new password must be different from the current password.";
export const REFUSED_NEW_PASSWORD_MESSAGE = "The new password was not accepted. Choose one that meets every rule above.";
// Only what is certain: the API answers 204 also when the confirmation email could not be sent.
export const PASSWORD_CHANGED_MESSAGE = "Your password is changed.";
export const PASSWORD_CHANGE_FAILED_MESSAGE = "Your password could not be changed. Try again.";

/**
 * Changing a password while signed in.
 *
 * It asks for the current password, so that a session left open on somebody else's screen is not
 * enough to take the account. A wrong current password is an error on that field: read as a
 * generic failure, it sent people to retype a new password that was fine.
 */
export default function PasswordSettingsSection() {
  // A new key empties every field after a change, including the ones PasswordInput keeps itself.
  const [formKey, setFormKey] = useState(0);

  return (
    <SettingsSection
      description="Change the password you sign in with. You need your current password. We send you an email when it changes."
      title="Password"
    >
      <ChangePasswordForm key={formKey} onChanged={() => setFormKey((key) => key + 1)} />
    </SettingsSection>
  );
}

function ChangePasswordForm(props: Readonly<{ onChanged: () => void }>) {
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState<Password>();
  const [currentPasswordError, setCurrentPasswordError] = useState<string>();
  const [newPasswordError, setNewPasswordError] = useState<string>();

  const canSubmit = currentPassword.length > 0 && newPassword !== undefined && !isLoading;

  async function handleSubmit(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!newPassword) return;

    setCurrentPasswordError(undefined);
    setNewPasswordError(undefined);
    if (newPassword.value === currentPassword) {
      setNewPasswordError(UNCHANGED_PASSWORD_MESSAGE);
      return;
    }

    try {
      await changePassword({
        currentPassword,
        newPassword: newPassword.value,
        // PasswordInput reports a password only once its confirmation matches.
        confirmPassword: newPassword.value,
      }).unwrap();
      enqueueSnackbar(PASSWORD_CHANGED_MESSAGE, { variant: "success" });
      props.onChanged();
    } catch (error) {
      const { fieldErrors } = readRtkQueryError(error as Parameters<typeof readRtkQueryError>[0]);
      // The API names the field and keeps its own words back, so the words are ours.
      if (fieldErrors.currentPassword) setCurrentPasswordError(WRONG_CURRENT_PASSWORD_MESSAGE);
      if (fieldErrors.newPassword) setNewPasswordError(REFUSED_NEW_PASSWORD_MESSAGE);
      if (!fieldErrors.currentPassword && !fieldErrors.newPassword) {
        enqueueSnackbar(PASSWORD_CHANGE_FAILED_MESSAGE, { variant: "error" });
      }
    }
  }

  return (
    <Box
      aria-label="Change password"
      component="form"
      noValidate
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      <TextField
        autoComplete="current-password"
        error={currentPasswordError !== undefined}
        helperText={currentPasswordError}
        id="settings-current-password-input"
        label="Current Password"
        onChange={(event) => {
          setCurrentPassword(event.target.value);
          setCurrentPasswordError(undefined);
        }}
        required
        sx={{ maxWidth: { sm: 360 } }}
        type="password"
        value={currentPassword}
      />
      <PasswordInput
        confirm
        idPrefix="settings"
        onChange={(password) => {
          setNewPassword(password);
          setNewPasswordError(undefined);
        }}
      />
      {newPasswordError && (
        <FormHelperText error id="settings-new-password-error" role="alert">
          {newPasswordError}
        </FormHelperText>
      )}
      <div>
        <Button disabled={!canSubmit} loading={isLoading} type="submit" variant="contained">
          Change password
        </Button>
      </div>
    </Box>
  );
}

